import React, { useState } from 'react';
import { SALARY_CONFIG } from '../data/projects';
import {
  getProjectRawShared, getProjectTotalShared, getDevProjectContribution,
  getMonthlyTeamCost, monthsBetween, formatCurrency, formatAED,
  STATUS_CONFIG, getPortfolioTotals, getDevById,
} from '../utils';

// Two-line currency display — L.E on top, AED below
function Money({ n, size = 13, gold = false }) {
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: size, color: gold ? '#fbbf24' : '#e2e8f0', lineHeight: 1.2 }}>
        {formatCurrency(n)}
      </div>
      <div style={{ fontSize: size - 2, color: '#64748b', marginTop: 1 }}>
        ≈ {formatAED(n)}
      </div>
    </div>
  );
}

function EditableField({ display, value, type = 'text', onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  function start() { setVal(value || ''); setEditing(true); }
  function save() {
    if (val.trim() !== (value || '').toString()) onSave(val.trim());
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus type={type} value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        placeholder={placeholder}
        style={{
          background: '#1e293b', border: '1px solid #3b82f6', borderRadius: 4,
          color: '#fbbf24', fontSize: 13, fontWeight: 700,
          padding: '2px 6px', outline: 'none', fontFamily: 'inherit', width: '100%',
        }}
      />
    );
  }
  return (
    <span onClick={start} style={{ cursor: 'text', borderBottom: '1px dotted #475569' }}>
      {display || <span style={{ color: '#334155' }}>— set</span>}
    </span>
  );
}

function DevCard({ dev, onUpdate }) {
  const [salEditing, setSalEditing] = useState(false);
  const [salVal, setSalVal]         = useState('');

  function startSal() { setSalVal(String(dev.monthlySalary)); setSalEditing(true); }
  function saveSal() {
    const n = parseInt(salVal.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(n)) onUpdate(dev.id, { monthlySalary: n });
    setSalEditing(false);
  }

  const hasLeft   = dev.endDate && new Date(dev.endDate) < new Date();
  const isLeaving = dev.endDate && !hasLeft;
  const badgeColor = hasLeft ? '#78716c' : isLeaving ? '#f59e0b' : '#22c55e';
  const badgeBg    = hasLeft ? '#1c1917'  : isLeaving ? '#451a03' : '#052e16';
  const badgeText  = hasLeft ? 'Left'     : isLeaving ? 'Leaving' : 'Active';

  return (
    <div style={{
      background: '#0f172a', border: '1px solid #1e293b',
      borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 8,
      opacity: hasLeft ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#f1f5f9' }}>{dev.name}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{dev.role}</div>
        </div>
        <div style={{
          background: badgeBg, color: badgeColor,
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
          border: `1px solid ${badgeColor}44`,
        }}>{badgeText}</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(dev.stack || []).map(s => (
          <span key={s} style={{
            fontSize: 10, padding: '1px 7px', borderRadius: 4,
            background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
          }}>{s}</span>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8, borderTop: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 2 }}>Monthly Salary <span style={{ color: '#334155' }}>(click)</span></div>
            {salEditing ? (
              <input
                autoFocus type="text" value={salVal}
                onChange={e => setSalVal(e.target.value)}
                onBlur={saveSal}
                onKeyDown={e => { if (e.key === 'Enter') saveSal(); if (e.key === 'Escape') setSalEditing(false); }}
                style={{
                  background: '#1e293b', border: '1px solid #3b82f6', borderRadius: 4,
                  color: '#fbbf24', fontSize: 15, fontWeight: 800,
                  width: 130, padding: '2px 6px', outline: 'none', fontFamily: 'inherit',
                }}
              />
            ) : (
              <div onClick={startSal} style={{ cursor: 'text' }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fbbf24', borderBottom: '1px dotted #92400e', display: 'inline-block' }}>
                  {dev.monthlySalary > 0 ? formatCurrency(dev.monthlySalary) : '— set salary'}
                </div>
                {dev.monthlySalary > 0 && (
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>≈ {formatAED(dev.monthlySalary)}</div>
                )}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#475569' }}>Joined</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
              {new Date(dev.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: '#475569', marginBottom: 2 }}>Last Day <span style={{ color: '#334155' }}>(click to set)</span></div>
            <EditableField
              display={dev.endDate ? new Date(dev.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
              value={dev.endDate || ''}
              type="date"
              onSave={v => onUpdate(dev.id, { endDate: v || null })}
            />
          </div>
          {dev.endDate && (
            <button onClick={() => onUpdate(dev.id, { endDate: null })} style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 4,
              background: 'transparent', border: '1px solid #334155',
              color: '#475569', cursor: 'pointer', fontFamily: 'inherit',
            }}>clear</button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddDevForm({ onAdd, onCancel }) {
  const [form, setForm] = useState({ name: '', role: '', stack: '', monthlySalary: '', startDate: '', endDate: '' });
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function submit() {
    if (!form.name.trim() || !form.startDate) return;
    onAdd({
      name:          form.name.trim(),
      role:          form.role.trim(),
      stack:         form.stack.split(',').map(s => s.trim()).filter(Boolean),
      monthlySalary: parseInt(form.monthlySalary, 10) || 0,
      startDate:     form.startDate,
      endDate:       form.endDate || null,
    });
  }
  const inp = {
    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6,
    color: '#e2e8f0', fontSize: 12, padding: '7px 10px', outline: 'none',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  };
  return (
    <div style={{ background: '#0a0f1e', border: '1px solid #1e3a6e', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', letterSpacing: 1 }}>NEW TEAM MEMBER</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Full Name *</div><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Ahmed Hassan" /></div>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Role / Title</div><input style={inp} value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. AI Engineer" /></div>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Stack (comma-separated)</div><input style={inp} value={form.stack} onChange={e => set('stack', e.target.value)} placeholder="Python, TensorFlow, LLMs" /></div>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Monthly Salary (L.E)</div><input style={inp} type="number" value={form.monthlySalary} onChange={e => set('monthlySalary', e.target.value)} placeholder="0" /></div>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Join Date *</div><input style={inp} type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></div>
        <div><div style={{ fontSize: 10, color: '#475569', marginBottom: 4 }}>Last Day (optional)</div><input style={inp} type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={submit} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>Add to Team</button>
        <button onClick={onCancel} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #1e293b', cursor: 'pointer', background: 'transparent', color: '#64748b', fontSize: 12, fontFamily: 'inherit' }}>Cancel</button>
      </div>
    </div>
  );
}

function ProjectBurnRow({ project, index, allProjects, developers }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG['planned'];
  const raw     = getProjectRawShared(project, allProjects, developers);
  const total   = getProjectTotalShared(project, allProjects, developers);
  const monthly = getMonthlyTeamCost(project, developers);
  const months  = monthsBetween(project.startDate, project.endDate);
  const isMain  = project.type === 'main';
  const isSub   = project.type === 'sub';

  return (
    <>
      <tr onClick={() => setExpanded(e => !e)} style={{
        cursor: 'pointer',
        background: expanded ? '#0d1f3c' : (index % 2 === 0 ? '#0a0f1e' : '#0d1117'),
        transition: 'background 0.1s',
      }}>
        <td style={{ padding: '10px 12px', paddingLeft: isSub ? 28 : 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: isMain ? 14 : 11 }}>{isMain ? '🔷' : isSub ? '↳' : '◼'}</span>
            <div>
              <div style={{ fontWeight: isMain ? 800 : 600, fontSize: isMain ? 13 : 12, color: isMain ? '#e2e8f0' : '#cbd5e1' }}>{project.name}</div>
              {project.phase && <div style={{ fontSize: 10, color: '#475569' }}>{project.phase}</div>}
            </div>
          </div>
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: statusCfg.bg + '22', color: statusCfg.color, border: `1px solid ${statusCfg.color}44` }}>{statusCfg.label}</span>
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>
          {new Date(project.startDate).toLocaleDateString('en-US',{month:'short',year:'numeric'})} → {new Date(project.endDate).toLocaleDateString('en-US',{month:'short',year:'numeric'})}
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b', fontSize: 12 }}>{months} mo</td>
        <td style={{ padding: '10px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>{project.assignedDevIds.length}</td>
        <td style={{ padding: '10px 8px', textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{formatCurrency(monthly)}/mo</div>
          <div style={{ fontSize: 10, color: '#475569' }}>{formatAED(monthly)}/mo</div>
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'right' }}>
          <Money n={raw} />
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'right' }}>
          <Money n={total} gold />
        </td>
        <td style={{ padding: '10px 8px', textAlign: 'center', color: '#334155', fontSize: 11 }}>{expanded ? '▲' : '▼'}</td>
      </tr>

      {expanded && (
        <tr style={{ background: '#0d1f3c' }}>
          <td colSpan={9} style={{ padding: '12px 16px 16px 32px' }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>DEVELOPER CONTRIBUTION</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Developer', 'Rate/mo', 'Allocated (L.E)', 'Allocated (AED)'].map(h => (
                        <th key={h} style={{ textAlign: 'left', color: '#334155', fontSize: 10, padding: '3px 8px', borderBottom: '1px solid #1e293b' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {project.assignedDevIds.map(devId => {
                      const dev = getDevById(devId, developers);
                      if (!dev) return null;
                      const contrib = getDevProjectContribution(devId, project, allProjects, developers);
                      return (
                        <tr key={devId}>
                          <td style={{ padding: '4px 8px', color: '#e2e8f0', fontWeight: 600 }}>{dev.name}</td>
                          <td style={{ padding: '4px 8px', color: '#94a3b8' }}>{formatCurrency(dev.monthlySalary)}</td>
                          <td style={{ padding: '4px 8px', color: '#fbbf24', fontWeight: 700 }}>{formatCurrency(contrib)}</td>
                          <td style={{ padding: '4px 8px', color: '#94a3b8' }}>{formatAED(contrib)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>COST SUMMARY</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    ['Raw Burn', raw],
                    [`Overhead (+${SALARY_CONFIG.overheadPct * 100}%)`, Math.round(raw * SALARY_CONFIG.overheadPct)],
                    ['Total', total],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '6px 10px', background: '#0a0f1e', borderRadius: 6 }}>
                      <span style={{ color: '#475569', fontSize: 12 }}>{label}</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: label === 'Total' ? '#fbbf24' : '#94a3b8', fontWeight: 700, fontSize: 12 }}>{formatCurrency(val)}</div>
                        <div style={{ color: '#475569', fontSize: 10 }}>{formatAED(val)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {project.notes && (
                  <div style={{ marginTop: 12, padding: 8, background: '#0a0f1e', borderRadius: 6, fontSize: 11, color: '#64748b' }}>📝 {project.notes}</div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SalaryBurn({ projects, developers, onUpdateDev, onAddDev }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const totals     = getPortfolioTotals(projects, developers);
  const activeDevs = developers.filter(d => d.status === 'Active' && (!d.endDate || new Date(d.endDate) >= new Date()));

  const mainProjects = projects.filter(p => p.type === 'main');
  const standalone   = projects.filter(p => p.type === 'standalone');
  const ordered = [];
  for (const mp of mainProjects) {
    ordered.push(mp);
    ordered.push(...projects.filter(p => p.parentId === mp.id));
  }
  ordered.push(...standalone);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24 }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
        {[
          { label: 'Monthly Payroll',     egp: totals.totalMonthlyPayroll, sub: 'Active team combined',    color: '#3b82f6', icon: '💰' },
          { label: 'Total Raw Burn',      egp: totals.totalRaw,            sub: 'Cost-shared, join-aware', color: '#f97316', icon: '🔥' },
          { label: 'Total with Overhead', egp: totals.totalOverhead,       sub: `Incl. ${SALARY_CONFIG.overheadPct * 100}% buffer`, color: '#fbbf24', icon: '📊' },
        ].map(card => (
          <div key={card.label} style={{
            background: '#0f172a', border: `1px solid ${card.color}33`,
            borderRadius: 12, padding: '18px 20px', borderTop: `3px solid ${card.color}`,
          }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{card.icon}</div>
            <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: card.color }}>{formatCurrency(card.egp)}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginTop: 2 }}>{formatAED(card.egp)}</div>
            <div style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
        <div style={{
          background: '#0f172a', border: '1px solid #22c55e33',
          borderRadius: 12, padding: '18px 20px', borderTop: '3px solid #22c55e',
        }}>
          <div style={{ fontSize: 20, marginBottom: 6 }}>👥</div>
          <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>ACTIVE TEAM</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#22c55e' }}>{activeDevs.length}</div>
          <div style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>
            {activeDevs.length ? `Avg ${formatCurrency(Math.round(totals.totalMonthlyPayroll / activeDevs.length))}/mo` : '—'}
          </div>
        </div>
      </div>

      {/* Project burn table */}
      <div style={{ background: '#0a0f1e', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#f1f5f9' }}>Project-Level Salary Breakdown</div>
          <div style={{ fontSize: 11, color: '#475569' }}>All amounts in L.E with AED equivalent · Click row to expand</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0d1117' }}>
                {['Project', 'Status', 'Timeline', 'Duration', 'Team', 'Monthly Rate', 'Raw Burn', 'With Overhead', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 8px', textAlign: h === 'Project' ? 'left' : 'center',
                    fontSize: 10, fontWeight: 700, color: '#334155',
                    letterSpacing: 0.8, borderBottom: '1px solid #1e293b', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.map((p, i) => (
                <ProjectBurnRow key={p.id} project={p} index={i} allProjects={projects} developers={developers} />
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#0d1f3c', borderTop: '2px solid #1e3a6e' }}>
                <td colSpan={6} style={{ padding: '12px', fontWeight: 800, color: '#94a3b8', fontSize: 12 }}>
                  TOTAL (standalone + sub-projects)
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                  <Money n={totals.totalRaw} />
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                  <Money n={totals.totalOverhead} gold />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Team roster */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#94a3b8', letterSpacing: 1 }}>
            TEAM ROSTER — click salary or last-day to edit
          </div>
          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)} style={{
              padding: '6px 16px', borderRadius: 6, border: '1px solid #3b82f6',
              background: 'transparent', color: '#3b82f6', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>+ Add Team Member</button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12, marginBottom: 16 }}>
          {developers.map(dev => <DevCard key={dev.id} dev={dev} onUpdate={onUpdateDev} />)}
        </div>
        {showAddForm && (
          <AddDevForm
            onAdd={dev => { onAddDev(dev); setShowAddForm(false); }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    </div>
  );
}
